// External Materials Database Integration Service
// Integrates OpenMaterialsDB and MatWeb for material property lookup
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

export interface MaterialProperty {
    name: string;
    value: string;
    unit?: string;
}

export interface ExternalMaterial {
    source: 'OpenMaterialsDB' | 'MatWeb' | 'Local';
    id: string;
    name: string;
    grade?: string;
    category: string;
    properties: MaterialProperty[];
    standards?: string[];
    description?: string;
    url?: string;
}

export interface LocalMaterialSpec {
    id: string;
    materialName: string;
    grade?: string;
    standard?: string;
    properties: Record<string, string>;
    linkedBomIds?: number[];
    supplier?: string;
    certificationRequired?: boolean;
    validationStatus?: string;
    generation?: number;
    cost?: { value: number; currency: string };
    leadTimeWeeks?: number;
    testDataIds?: string[];
}

/**
 * Search OpenMaterialsDB (Swedish materials database)
 * API Documentation: https://openmaterialsdb.se/api/
 */
export const searchOpenMaterialsDB = async (searchTerm: string): Promise<ExternalMaterial[]> => {
    try {
        // OpenMaterialsDB REST API endpoint
        const apiUrl = `https://openmaterialsdb.se/api/materials/search?q=${encodeURIComponent(searchTerm)}`;

        const data = await authenticatedFetchJSON(apiUrl, {
            method: 'GET',
            skipAuth: true,
            headers: {
                'Accept': 'application/json',
            }
        });

        // Transform OpenMaterialsDB format to our standard format
        return data.materials?.map((mat: any) => ({
            source: 'OpenMaterialsDB' as const,
            id: mat.id || mat.uuid,
            name: mat.name || mat.designation,
            grade: mat.grade,
            category: mat.material_class || 'Unknown',
            properties: extractPropertiesFromOpenMaterialsDB(mat),
            standards: mat.standards || [],
            description: mat.description,
            url: `https://openmaterialsdb.se/material/${mat.id}`
        })) || [];

    } catch (error) {
        console.error('OpenMaterialsDB search error:', error);
        return [];
    }
};

/**
 * Search MatWeb (comprehensive materials database)
 * Note: MatWeb doesn't have a public API, so we'll use web scraping or direct links
 */
export const searchMatWeb = async (searchTerm: string): Promise<ExternalMaterial[]> => {
    // MatWeb doesn't have a public REST API
    // We'll construct search URLs and provide guidance to user

    const searchUrl = `https://www.matweb.com/search/QuickText.aspx?SearchText=${encodeURIComponent(searchTerm)}`;

    console.log('MatWeb search URL:', searchUrl);

    // Return a placeholder that directs users to manual search
    return [{
        source: 'MatWeb' as const,
        id: 'manual-search',
        name: `Search MatWeb for "${searchTerm}"`,
        category: 'Manual Search',
        properties: [],
        description: `MatWeb doesn't provide a public API. Click to search manually.`,
        url: searchUrl
    }];
};

/**
 * Search local database (seed data)
 */
export const searchLocalMaterials = (
    searchTerm: string,
    localMaterials: LocalMaterialSpec[]
): ExternalMaterial[] => {
    const searchLower = searchTerm.toLowerCase();

    return localMaterials
        .filter(mat =>
            mat.materialName.toLowerCase().includes(searchLower) ||
            mat.id.toLowerCase().includes(searchLower) ||
            mat.grade?.toLowerCase().includes(searchLower)
        )
        .map(mat => ({
            source: 'Local' as const,
            id: mat.id,
            name: mat.materialName,
            grade: mat.grade,
            category: 'Local Database',
            properties: Object.entries(mat.properties).map(([name, value]) => ({
                name,
                value
            })),
            standards: mat.standard ? [mat.standard] : [],
            description: `Supplier: ${mat.supplier || 'N/A'}, Cost: ${mat.cost?.value} ${mat.cost?.currency || ''}`
        }));
};

/**
 * Extract material properties from OpenMaterialsDB format
 */
function extractPropertiesFromOpenMaterialsDB(mat: any): MaterialProperty[] {
    const properties: MaterialProperty[] = [];

    // Common material properties mapping
    const propertyMap: Record<string, string> = {
        'density': 'Density',
        'youngs_modulus': 'Young\'s Modulus',
        'tensile_strength': 'Tensile Strength',
        'yield_strength': 'Yield Strength',
        'elongation': 'Elongation',
        'hardness': 'Hardness',
        'thermal_conductivity': 'Thermal Conductivity',
        'specific_heat': 'Specific Heat',
        'melting_point': 'Melting Point',
        'poissons_ratio': 'Poisson\'s Ratio'
    };

    // Extract properties from various possible locations in the response
    if (mat.properties) {
        Object.entries(mat.properties).forEach(([key, value]: [string, any]) => {
            const displayName = propertyMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            properties.push({
                name: displayName,
                value: value.value?.toString() || value.toString(),
                unit: value.unit
            });
        });
    }

    // Handle mechanical properties
    if (mat.mechanical) {
        Object.entries(mat.mechanical).forEach(([key, value]: [string, any]) => {
            properties.push({
                name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                value: value.value?.toString() || value.toString(),
                unit: value.unit
            });
        });
    }

    return properties;
}

/**
 * Convert external material to local format for import
 */
export const convertToLocalMaterial = (
    external: ExternalMaterial,
    options: {
        supplier?: string;
        cost?: { value: number; currency: string };
        leadTimeWeeks?: number;
        linkedBomIds?: number[];
    } = {}
): LocalMaterialSpec => {
    // Generate local ID based on naming convention
    const categoryPrefix = external.category.substring(0, 3).toUpperCase();
    const namePrefix = external.name.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, '');
    const localId = `MAT-${categoryPrefix}-${namePrefix}-${Date.now().toString().slice(-3)}`;

    // Convert property array to record
    const propertiesRecord: Record<string, string> = {};
    external.properties.forEach(prop => {
        const value = prop.unit ? `${prop.value} ${prop.unit}` : prop.value;
        propertiesRecord[prop.name] = value;
    });

    return {
        id: localId,
        materialName: external.name,
        grade: external.grade,
        standard: external.standards?.[0],
        properties: propertiesRecord,
        supplier: options.supplier || `Imported from ${external.source}`,
        certificationRequired: false,
        validationStatus: 'pending',
        generation: 0,
        cost: options.cost,
        leadTimeWeeks: options.leadTimeWeeks,
        linkedBomIds: options.linkedBomIds || [],
        testDataIds: []
    };
};

/**
 * Comprehensive material search across all sources
 */
export const searchAllMaterialDatabases = async (
    searchTerm: string,
    localMaterials: LocalMaterialSpec[] = []
): Promise<{
    openMaterialsDB: ExternalMaterial[];
    matWeb: ExternalMaterial[];
    local: ExternalMaterial[];
}> => {
    const [openDB, matWeb] = await Promise.all([
        searchOpenMaterialsDB(searchTerm),
        searchMatWeb(searchTerm)
    ]);

    const local = searchLocalMaterials(searchTerm, localMaterials);

    return {
        openMaterialsDB: openDB,
        matWeb: matWeb,
        local: local
    };
};

/**
 * Export material to materials-seed.ts format
 */
export const exportMaterialToSeedFormat = (material: LocalMaterialSpec): string => {
    // Pre-calculate values to reduce complexity of the template literal expression
    const grade = material.grade || '';
    const standard = material.standard || '';
    const linkedBomIds = material.linkedBomIds?.join(', ') || '';
    const supplier = material.supplier || '';
    const isCertRequired = material.certificationRequired || false;
    const validationStatus = material.validationStatus || 'pending';
    const generation = material.generation || 0;
    const costValue = material.cost?.value || 0;
    const costCurrency = material.cost?.currency || 'USD';
    const leadTime = material.leadTimeWeeks || 0;
    const testDataIds = material.testDataIds?.map((id: string) => `'${id}'`).join(', ') || '';

    // Format properties
    const propertiesStr = Object.entries(material.properties)
        .map(([key, value]) => `            '${key}': '${value}'`)
        .join(',\n');

    return `    {
        id: '${material.id}',
        materialName: '${material.materialName}',
        grade: '${grade}',
        standard: '${standard}',
        properties: {
${propertiesStr}
        },
        linkedBomIds: [${linkedBomIds}],
        supplier: '${supplier}',
        certificationRequired: ${isCertRequired},
        validationStatus: '${validationStatus}',
        generation: ${generation},
        cost: { value: ${costValue}, currency: '${costCurrency}' },
        leadTimeWeeks: ${leadTime},
        testDataIds: [${testDataIds}]
    }`;
};
